// Activity log — the company-wide audit feed (every configuration change and
// operational action). Listed via POST activity-logs/list with server-side
// pagination, search and the confirmed facet filters (module/action/userId id
// arrays + the created/createdStart/createdEnd date window + the projectIds
// scope the project-detail Activity card sends). Still NO orderBy — sorting
// stays off — and export has no endpoint yet.
//
// `description` arrives as a READY-TO-DISPLAY sentence ("Changed project
// "Security proj" status to archived"), so the target cell renders it
// literally — backend data values stay literal, no i18n keys for them.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import { FormDateFilter } from './formsApi';

/** Action slugs, verbatim from the backend's ActivityLogAction enum. */
export enum ActivityLogAction {
  VIEW = 'view',
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
  EXPORT = 'export',
  PUBLISH = 'publish',
  APPROVE = 'approve',
  ASSIGN = 'assign',
  ASSIGN_ACROSS_INSPECTIONS = 'assign_across_inspections',
  ASSIGN_ACROSS_TASKS = 'assign_across_tasks',
  ASSIGN_ACROSS_TICKETS = 'assign_across_tickets',
  ASSIGN_ACROSS_PROJECTS = 'assign_across_projects',
  RESOLVE = 'resolve',
  SHARING = 'sharing',
  IMPORT = 'import',
  INVITE = 'invite',
  DEACTIVATE = 'deactivate',
  SETUP = 'setup',
  FORM_STRUCTURE = 'form_structure',
  COMPANY_SQL = 'company_sql',
  ATTENDANCE = 'attendance',
  TASKS = 'tasks',
  PEOPLE = 'people',
  POWERBI_VIEW = 'powerbi_view',
  FILL = 'fill',
  // Domain-specific attendance actions (no matching permission slug).
  CLOCK_IN = 'clock_in',
  CLOCK_OUT = 'clock_out',
  BREAK_START = 'break_start',
  BREAK_END = 'break_end',
  /** A lifecycle state/status flip (suspend/resume/archive/restore). */
  STATUS_CHANGED = 'status_changed',
}

export interface ActivityUser {
  id: string;
  fullName: string;
  /** The actor's company-user title (e.g. "Company Owner"), shown under the
   *  name. Absent when the wire doesn't carry it. */
  title?: string;
}

/** One selected module in a create/delete role snapshot: the module title and
 *  the permissions the role grants for it (selected only). */
export interface ActivityPermissionSelection {
  module: string;
  type: string;
  selected: string[];
}

/** One module whose permissions changed in a role update — the delta only. */
export interface ActivityPermissionChange {
  module: string;
  type: string;
  added: string[];
  removed: string[];
}

/** A single scalar field edit on an update — both the old and new value. */
export interface ActivityFieldChange {
  from?: string;
  to?: string;
}

/** A membership delta (people or tags) — labels added and removed. */
export interface ActivityListChange {
  added: string[];
  removed: string[];
}

/** A data-source column delta on an update — column names added / updated /
 *  removed, plus a rename (before → after) when a column title changed. */
export interface ActivityColumnsChange {
  added?: string[];
  updated?: string[];
  removed?: string[];
  renamed?: ActivityFieldChange;
}

/** A form-set option delta on an update — option values added/updated/removed. */
export interface ActivityOptionsChange {
  added?: string[];
  updated?: string[];
  removed?: string[];
}

/** Action-shaped `details` payload. Open by design (shape varies per module and
 *  action); each module snapshots its own domain-relevant keys below. */
export interface ActivityDetails {
  /** Entity name/title snapshot (create/delete). Each module writes whichever
   *  of these names its entity uses. */
  name?: string;
  title?: string;
  fieldTitle?: string;
  schemaName?: string;
  /** Scalar edits on an update, field → { from, to } (only changed fields). */
  changes?: Record<string, ActivityFieldChange>;
  /** Selected permissions per module (roles create/delete). */
  permissions?: ActivityPermissionSelection[];
  /** Added/removed permissions per module (roles update). */
  permissionChanges?: ActivityPermissionChange[];
  /** People added/removed (company structure / inspection / group update). */
  people?: ActivityListChange;
  /** Tags added/removed (structure / inspection create/update). */
  tags?: ActivityListChange;
  /** Groups added/removed (project member update). */
  groups?: ActivityListChange;
  /** Column changes (data source add/update/rename/delete column). */
  columns?: ActivityColumnsChange;
  /** Option changes (form set update). */
  options?: ActivityOptionsChange;
  /** How many questions a form-structure save added/removed — explains an edit
   *  whose total count did not change (one removed, one added). */
  questions?: { added?: number; removed?: number };
  /** Row tally of a deleted data source ("1,204 rows → Deleted"). */
  rows?: number;
  /** Create-time scalar attributes snapshot (rendered as value rows). */
  dataSourceType?: string;
  priority?: string;
  sqlColumn?: string;
  type?: string;
  color?: string;
  state?: string;
  externalId?: string;
  dataSourceColumn?: string;
  isHidden?: boolean;
  [key: string]: unknown;
}

export interface ActivityItem {
  id: string;
  /** Null when the actor's name is blank — the cell dashes. */
  user: ActivityUser | null;
  /** Confirmed action slug — chipped via the complete ACTION_META. */
  action: ActivityLogAction;
  /** Module slug (e.g. 'projects') — drives the target tile icon. */
  module: string;
  /** 'admin' confirmed; kept open — an operational variant likely exists. */
  moduleType: string;
  /** Ready-to-display sentence from the wire — rendered literally. */
  description: string;
  /** The acted-on entity's id (e.g. the role id) — used for the target code. */
  entityId: string;
  /** The acted-on entity's own reference number (e.g. a role's 3 → "ROLE-03"),
   *  when it has one. Drives the target code. */
  entityReferenceNumber?: number;
  status: string;
  /** ISO. */
  createdAt: string;
  /** Per-action change payload rendered in the expanded detail panel. */
  details?: ActivityDetails;
}

export interface ActivityLogPage {
  items: ActivityItem[];
  total: number;
}

/** POST activity-logs/list body — every optional param is omitted (undefined)
 *  when unset, which JSON.stringify drops. */
export interface ActivityLogQueryArgs {
  search?: string;
  /** Module slugs (as sent on the wire, e.g. 'projects'). */
  module?: string[];
  /** Action slugs. */
  action?: ActivityLogAction[];
  /** Acting company-user ids. */
  userId?: string[];
  /** Scope to these projects' events (the project-detail Activity card sends
   *  one id). Omitted = company-wide. */
  projectIds?: string[];
  /** Created-date preset — omitted for "Any time"; unlike the forms lists, the
   *  backend takes no last_90_days. */
  created?: Exclude<FormDateFilter, FormDateFilter.LAST_90_DAYS>;
  /** ISO datetime edges, sent only while created === 'custom'. */
  createdStart?: string;
  createdEnd?: string;
  page: number;
  limit: number;
}

// ---- raw shapes (per the confirmed response) ----
interface ApiActivityPerson {
  _id: string;
  user: { _id: string; firstName: string; lastName: string };
  /** Populated alongside the user (backend selects `user title`). */
  title?: string;
}
interface ApiActivityItem {
  _id: string;
  company: string;
  companyUser: ApiActivityPerson;
  action: ActivityLogAction;
  module: string;
  moduleType: string;
  entityId: string;
  entityReferenceNumber?: number;
  description: string;
  path: string;
  status: string;
  /** Shape varies per action; the roles module carries a permission diff. */
  details?: ActivityDetails;
  createdAt: string;
  updatedAt: string;
}
interface ApiActivityLogPage {
  items: ApiActivityItem[];
  totalRecords: number;
}

// Same nested { user: { firstName, lastName } } actor shape as the datasource
// and submissions payloads. A blank name is null, so the cell dashes instead of
// rendering a lone space.
const toActor = (p: ApiActivityPerson): ActivityUser | null => {
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName, title: p.title || undefined } : null;
};

const toActivityItem = (a: ApiActivityItem): ActivityItem => ({
  id: a._id,
  user: toActor(a.companyUser),
  action: a.action,
  module: a.module,
  moduleType: a.moduleType,
  description: a.description,
  entityId: a.entityId,
  entityReferenceNumber: a.entityReferenceNumber,
  status: a.status,
  createdAt: a.createdAt,
  details: a.details,
});

export const activityLogApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getActivityLogs: build.query<ActivityLogPage, ActivityLogQueryArgs>({
      query: (body) => ({ ...ROUTES.activityLogs.list, data: body }),
      transformResponse: (res: ApiActivityLogPage) => ({
        items: res.items.map(toActivityItem),
        total: res.totalRecords,
      }),
      providesTags: [{ type: 'ActivityLogs', id: 'LIST' }],
    }),
  }),
});

export const { useGetActivityLogsQuery } = activityLogApi;
